// Customizable Area Start
import { BlockComponent } from "../../../framework/src/BlockComponent";
import { runEngine } from "../../../framework/src/RunEngine";
import { Message } from "../../../framework/src/Message";
import { IBlock } from "../../../framework/src/IBlock";
import MessageEnum, {
  getName,
} from "../../../framework/src/Messages/MessageEnum";
import { makeApiMessage } from "../../../components/src/common";
import { navigateTo } from "../../utilities/src/CustomBlockHelpers";

export interface IMobileServiceRequest {
  id: string;
  type: string;
  attributes: {
    id: number;
    request_number: string;
    status: string;
    pickup_type: string;
    pickup_scheduled_at: string | null;
    delivery_type: string;
    delivery_scheduled_at: string | null;
    notes: string | null;
    cancellation_reason: string | null;
    order_id: number | null;
    created_at: string;
    updated_at: string;
    customer: {
      id: number;
      full_name: string;
      phone_number: string;
    };
    mobile_service: {
      id: number;
      name: string;
      name_ar: string;
    };
    pickup_address: {
      id: number;
      label: string;
      address: string;
      location: string;
      latitude: string;
      longitude: string;
    } | null;
    delivery_address: {
      id: number;
      label: string;
      address: string;
      location: string;
      latitude: string;
      longitude: string;
    } | null;
    store: {
      id: number | null;
      name: string | null;
    };
    assigned_driver: {
      id: number;
      name: string;
    } | null;
  };
}

export const STATUS_OPTIONS = [
  "pending",
  "accepted",
  "pickup_in_progress",
  "picked_up",
  "order_created",
  "ready",
  "out_for_delivery",
  "delivered",
  "cancelled",
];

export const configJSON = require("./config");

export interface Props {
  navigation: any;
  id: string;
}

interface S {
  page: number;
  pageSize: number;
  totalCount: number;
  requestList: IMobileServiceRequest[];
  isLoading: boolean;
  snackbarOpen: boolean;
  snackbarMessage: string;
  snackbarSeverity: "success" | "error";
  statusMenuAnchor: HTMLElement | null;
  selectedRequestId: string | null;
  detailDialogOpen: boolean;
  detailData: IMobileServiceRequest | null;
  detailLoading: boolean;
  storeId: number;
}

interface SS {}

export default class MobileRequestsController extends BlockComponent<
  Props,
  S,
  SS
> {
  // Customizable Area Start
  getRequestsMessageId: string = "";
  updateStatusMessageId: string = "";
  getDetailMessageId: string = "";
  // Customizable Area End

  constructor(props: Props) {
    super(props);
    this.subScribedMessages = [
      getName(MessageEnum.RestAPIResponceMessage),
      getName(MessageEnum.LayoutDataMessage),
      getName(MessageEnum.BroadcastNavbarDataMessage),
    ];

    this.state = {
      page: 1,
      pageSize: 10,
      totalCount: 0,
      requestList: [],
      isLoading: false,
      snackbarOpen: false,
      snackbarMessage: "",
      snackbarSeverity: "success",
      statusMenuAnchor: null,
      selectedRequestId: null,
      detailDialogOpen: false,
      detailData: null,
      detailLoading: false,
      storeId: -1,
    };

    this.receive = this.receive.bind(this);
    runEngine.attachBuildingBlock(this as IBlock, this.subScribedMessages);
  }

  async receive(from: string, message: Message) {
    if (message.id === getName(MessageEnum.BroadcastNavbarDataMessage)) {
      const recievedData = message.getData(
        getName(MessageEnum.BroadcastNavbarData)
      );
      if (recievedData?.singleStoreID) {
        const newStoreId = Number(recievedData.singleStoreID);
        this.setState({ storeId: newStoreId, page: 1 }, () => {
          this.getRequestsList(1);
        });
      }
    }

    if (message.id === getName(MessageEnum.LayoutDataMessage)) {
      const recievedData = message.getData(
        getName(MessageEnum.LayoutMessageData)
      );
      if (recievedData?.userContext) {
        const storeId =
          recievedData.userContext?.user?.attributes?.employee_proifle?.data
            ?.attributes?.store_management?.store_id;
        if (storeId && this.state.storeId < 0) {
          this.setState({ storeId: Number(storeId) }, () => {
            this.getRequestsList();
          });
        }
      }
    }

    if (getName(MessageEnum.RestAPIResponceMessage) !== message.id) return;

    const requestId = message.getData(
      getName(MessageEnum.RestAPIResponceDataMessage)
    );
    const responseJson = message.getData(
      getName(MessageEnum.RestAPIResponceSuccessMessage)
    );

    if (requestId === this.getRequestsMessageId) {
      this.handleRequestsResponse(responseJson);
    }
    if (requestId === this.updateStatusMessageId) {
      this.handleUpdateStatusResponse(responseJson);
    }
    if (requestId === this.getDetailMessageId) {
      this.handleDetailResponse(responseJson);
    }
  }

  async componentDidMount() {
    super.componentDidMount();
  }

  handleRequestsResponse(response: any) {
    if (response?.data?.data) {
      this.setState({
        requestList: response.data.data,
        totalCount: response.meta?.total_count || 0,
        isLoading: false,
      });
    } else {
      this.setState({ requestList: [], isLoading: false });
    }
  }

  handleUpdateStatusResponse(response: any) {
    if (response?.data) {
      this.setState({
        snackbarOpen: true,
        snackbarMessage: "Status updated successfully",
        snackbarSeverity: "success",
        statusMenuAnchor: null,
        selectedRequestId: null,
      });
      this.getRequestsList();
    } else {
      this.setState({
        snackbarOpen: true,
        snackbarMessage: response?.error || "Failed to update status",
        snackbarSeverity: "error",
      });
    }
  }

  handleDetailResponse(response: any) {
    if (response?.data) {
      this.setState({
        detailData: response.data,
        detailLoading: false,
      });
    } else {
      this.setState({ detailLoading: false });
    }
  }

  getRequestsList = (page: number = this.state.page) => {
    if (this.state.storeId < 0) return;
    this.setState({ isLoading: true });
    const requestMessage = makeApiMessage({
      url: `bx_block_order_management/admin_mobile_service_requests?store_id=${this.state.storeId}&page=${page}&per=${this.state.pageSize}`,
      method: "GET",
    });
    this.getRequestsMessageId = requestMessage.messageId;
    runEngine.sendMessage(requestMessage.id, requestMessage);
  };

  updateRequestStatus = (id: string, status: string) => {
    const requestMessage = makeApiMessage({
      url: `bx_block_order_management/admin_mobile_service_requests/${id}/update_status`,
      method: "PUT",
      body: JSON.stringify({ status }),
    });
    this.updateStatusMessageId = requestMessage.messageId;
    runEngine.sendMessage(requestMessage.id, requestMessage);
  };

  getRequestDetail = (id: string) => {
    this.setState({ detailDialogOpen: true, detailLoading: true, detailData: null });
    const requestMessage = makeApiMessage({
      url: `bx_block_order_management/admin_mobile_service_requests/${id}`,
      method: "GET",
    });
    this.getDetailMessageId = requestMessage.messageId;
    runEngine.sendMessage(requestMessage.id, requestMessage);
  };

  handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    const page = newPage + 1;
    this.setState({ page }, () => this.getRequestsList(page));
  };

  handleStatusMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    requestId: string
  ) => {
    this.setState({
      statusMenuAnchor: event.currentTarget,
      selectedRequestId: requestId,
    });
  };

  handleStatusMenuClose = () => {
    this.setState({ statusMenuAnchor: null, selectedRequestId: null });
  };

  handleStatusSelect = (status: string) => {
    const { selectedRequestId } = this.state;
    if (selectedRequestId) {
      this.updateRequestStatus(selectedRequestId, status);
    }
    this.handleStatusMenuClose();
  };

  handleCloseDetail = () => {
    this.setState({ detailDialogOpen: false, detailData: null });
  };

  handleSnackbarClose = () => {
    this.setState({ snackbarOpen: false });
  };

  handleCreateOrder = (request: IMobileServiceRequest) => {
    const attrs = request.attributes;
    localStorage.setItem(
      "mobileServiceRequestData",
      JSON.stringify({
        mobile_service_request_id: attrs.id,
        customer_phone: attrs.customer.phone_number,
        customer_id: attrs.customer.id,
      })
    );
    navigateTo({ props: this.props, screenName: "OrderCreation" });
  };

  openMapNavigation = (latitude: string, longitude: string) => {
    window.open(
      `https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}`,
      "_blank"
    );
  };

  formatStatus = (status: string) => {
    return status
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  formatDate = (dateStr: string | null) => {
    if (!dateStr) return "--";
    const date = new Date(dateStr);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };
  // Customizable Area End
}
// Customizable Area End
